import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BookOpen,
  Headphones,
  Mic,
  PenTool,
  Plus,
  Trash2,
  CheckCircle2,
  Clock,
  Flame,
  Award,
  ExternalLink,
  Sparkles,
  Volume2,
  VolumeX,
  Play,
  RotateCcw,
  Sliders,
  Settings,
  X,
  ChevronRight,
  Globe,
  BarChart3,
  Calendar,
  AlertCircle,
  HelpCircle,
  Layers,
  Send,
  Loader2,
  RefreshCw,
  Check,
  Star,
  Target
} from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import {
  LanguageProfile,
  TargetLanguage,
  LanguageDailyPlan,
  LanguageDailyPlanItem,
  LanguagePracticeSession,
  LanguageSkillProgress,
  LanguageResource,
  LanguageSkillType,
  LanguageLevel,
  AiExerciseData,
  AiFeedbackData,
  DifficultyFeedback,
  UsefulnessFeedback,
  LanguageSkillAnalytics
} from '../types';
import {
  fetchLanguageBootstrap,
  saveLanguageProfile,
  saveTargetLanguage,
  deleteTargetLanguage,
  updateDailyPlan,
  recordPracticeSession,
  fetchPracticeHistory,
  addCustomResource,
  deleteResource,
  generateAiExercise,
  submitForAiFeedback,
  discoverDailyPlan,
  completePlanActivity,
  fetchLanguageAnalytics
} from '../lib/languageClient';
import { VoiceInputButton } from './VoiceInputButton';

const SUPPORTED_SOURCE_LANGS = [
  { code: 'fa', nameKey: 'langLearnLanguagePersian' },
  { code: 'en', nameKey: 'langLearnLanguageEnglish' },
  { code: 'sv', nameKey: 'langLearnLanguageSwedish' },
];

const PRESET_TARGET_LANGS = [
  { code: 'sv', nameKey: 'langLearnLanguageSwedish', flag: '🇸🇪', defaultName: 'Swedish' },
  { code: 'en', nameKey: 'langLearnLanguageEnglish', flag: '🇬🇧', defaultName: 'English' },
  { code: 'de', nameKey: 'langLearnLanguageGerman', flag: '🇩🇪', defaultName: 'German' },
  { code: 'fr', nameKey: 'langLearnLanguageFrench', flag: '🇫🇷', defaultName: 'French' },
  { code: 'es', nameKey: 'langLearnLanguageSpanish', flag: '🇪🇸', defaultName: 'Spanish' },
  { code: 'fa', nameKey: 'langLearnLanguagePersian', flag: '🇮🇷', defaultName: 'Persian' },
];

const LEVELS: LanguageLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1'];

export const LanguageLearningModule: React.FC = () => {
  const { t, lang, dir } = useLanguage();

  // Core Module State from D1
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<LanguageProfile | null>(null);
  const [targets, setTargets] = useState<TargetLanguage[]>([]);
  const [dailyPlans, setDailyPlans] = useState<LanguageDailyPlan[]>([]);
  const [skillProgress, setSkillProgress] = useState<LanguageSkillProgress[]>([]);
  const [resources, setResources] = useState<LanguageResource[]>([]);

  // Selected Target Language
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');

  // Active View Tab inside Module: 'plan' | 'resources' | 'stats'
  const [activeTab, setActiveTab] = useState<'plan' | 'resources' | 'stats'>('plan');

  // Modals
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [showAddTargetModal, setShowAddTargetModal] = useState<boolean>(false);
  const [showEditTargetModal, setShowEditTargetModal] = useState<boolean>(false);
  const [targetBeingEdited, setTargetBeingEdited] = useState<TargetLanguage | null>(null);
  const [editTargetLevel, setEditTargetLevel] = useState<LanguageLevel>('B1');
  const [editTargetGoal, setEditTargetGoal] = useState<number>(45);
  const [editTargetWeakAreas, setEditTargetWeakAreas] = useState<string>('');
  const [editTargetNotes, setEditTargetNotes] = useState<string>('');
  const [levelChangeFeedback, setLevelChangeFeedback] = useState<string | null>(null);
  const [showEditPlanModal, setShowEditPlanModal] = useState<boolean>(false);
  const [showAddResourceModal, setShowAddResourceModal] = useState<boolean>(false);

  // Smart Activity Completion Modal State
  const [completingItem, setCompletingItem] = useState<LanguageDailyPlanItem | null>(null);
  const [completionMinutes, setCompletionMinutes] = useState<number>(15);
  const [completionDifficulty, setCompletionDifficulty] = useState<DifficultyFeedback>('good');
  const [completionUsefulness, setCompletionUsefulness] = useState<UsefulnessFeedback>('very_useful');
  const [completionNotes, setCompletionNotes] = useState<string>('');
  const [isSubmittingCompletion, setIsSubmittingCompletion] = useState<boolean>(false);
  const [isDiscoveringPlan, setIsDiscoveringPlan] = useState<boolean>(false);
  const [targetAnalytics, setTargetAnalytics] = useState<LanguageSkillAnalytics | null>(null);

  // Interactive Practice Room Modal
  const [practiceSkill, setPracticeSkill] = useState<LanguageSkillType | null>(null);
  const [practiceDuration, setPracticeDuration] = useState<number>(10);
  const [isAiGenerating, setIsAiGenerating] = useState<boolean>(false);
  const [aiExercise, setAiExercise] = useState<AiExerciseData | null>(null);
  const [userSubmission, setUserSubmission] = useState<string>('');
  const [isAiEvaluating, setIsAiEvaluating] = useState<boolean>(false);
  const [aiFeedback, setAiFeedback] = useState<AiFeedbackData | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [sessionSaveSuccess, setSessionSaveSuccess] = useState<boolean>(false);

  // Practice History state
  const [historySessions, setHistorySessions] = useState<LanguagePracticeSession[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  // Onboarding Form State
  const [onboardingSource, setOnboardingSource] = useState<string>('fa');
  const [onboardingTarget, setOnboardingTarget] = useState<string>('sv');
  const [onboardingLevel, setOnboardingLevel] = useState<LanguageLevel>('A2');
  const [onboardingMinutes, setOnboardingMinutes] = useState<number>(45);

  // Add Target Form State
  const [newTargetCode, setNewTargetCode] = useState<string>('en');
  const [newTargetLevel, setNewTargetLevel] = useState<LanguageLevel>('A1');
  const [newTargetGoal, setNewTargetGoal] = useState<number>(30);

  // Resource Filter
  const [resourceSkillFilter, setResourceSkillFilter] = useState<string>('all');
  const [newResTitle, setNewResTitle] = useState<string>('');
  const [newResUrl, setNewResUrl] = useState<string>('');
  const [newResSource, setNewResSource] = useState<string>('');
  const [newResSkill, setNewResSkill] = useState<LanguageSkillType | 'all'>('all');
  const [newResLevel, setNewResLevel] = useState<string>('all');

  // --------------------------------------------------------------------------
  // 1. DATA BOOTSTRAP FROM CLOUDFLARE D1
  // --------------------------------------------------------------------------
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchLanguageBootstrap();
      
      if (!data.profile || data.targets.length === 0) {
        setShowOnboarding(true);
      } else {
        setProfile(data.profile);
        setTargets(data.targets);
        setDailyPlans(data.dailyPlans);
        setSkillProgress(data.skillProgress);
        setResources(data.resources);
        if (data.analytics) {
          setTargetAnalytics(data.analytics);
        }

        // Auto select first target language if none selected
        if (!selectedTargetId || !data.targets.some(t => t.id === selectedTargetId)) {
          setSelectedTargetId(data.targets[0].id);
        }
      }
    } catch (err: any) {
      console.error('Failed to load language data:', err);
      setError(err?.message || 'Error loading language learning data');
    } finally {
      setLoading(false);
    }
  }, [selectedTargetId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Current active target language object
  const currentTarget = useMemo(() => {
    return targets.find(t => t.id === selectedTargetId) || targets[0] || null;
  }, [targets, selectedTargetId]);

  // Current target's today plan with safely parsed smart items
  const currentPlan = useMemo(() => {
    if (!currentTarget) return null;
    const p = dailyPlans.find(p => p.target_language_id === currentTarget.id) || null;
    if (!p) return null;
    let items = p.items;
    if ((!items || !Array.isArray(items) || items.length === 0) && p.plan_items_json) {
      try {
        items = JSON.parse(p.plan_items_json);
      } catch {
        items = [];
      }
    }
    return {
      ...p,
      items: items || []
    };
  }, [dailyPlans, currentTarget]);

  // Current target's skill progress
  const currentSkillProgress = useMemo(() => {
    if (!currentTarget) return [];
    return skillProgress.filter(p => p.target_language_id === currentTarget.id);
  }, [skillProgress, currentTarget]);

  // Load history when switching to 'stats'
  useEffect(() => {
    if (activeTab === 'stats' && currentTarget) {
      setLoadingHistory(true);
      fetchPracticeHistory(currentTarget.id)
        .then(setHistorySessions)
        .catch(console.error)
        .finally(() => setLoadingHistory(false));
    }
  }, [activeTab, currentTarget]);

  // --------------------------------------------------------------------------
  // 2. ONBOARDING & TARGET MANAGEMENT
  // --------------------------------------------------------------------------
  const handleCompleteOnboarding = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const targetPreset = PRESET_TARGET_LANGS.find(p => p.code === onboardingTarget);
      const targetName = targetPreset ? t(targetPreset.nameKey, targetPreset.defaultName) : onboardingTarget;

      await saveLanguageProfile({
        source_language: onboardingSource,
        daily_availability_minutes: onboardingMinutes,
        target_languages: [
          {
            code: onboardingTarget,
            name: targetName,
            level: onboardingLevel,
            daily_goal_minutes: onboardingMinutes
          }
        ]
      });

      setShowOnboarding(false);
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTarget = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const preset = PRESET_TARGET_LANGS.find(p => p.code === newTargetCode);
      const name = preset ? t(preset.nameKey, preset.defaultName) : newTargetCode;

      const created = await saveTargetLanguage({
        language_code: newTargetCode,
        language_name: name,
        level: newTargetLevel,
        daily_goal_minutes: newTargetGoal
      });

      setShowAddTargetModal(false);
      await loadData();
      setSelectedTargetId(created.id);
    } catch (err: any) {
      alert(err?.message || 'Failed to add target language');
    }
  };

  const handleDeleteTarget = async (id: string) => {
    if (!confirm(t('confirmDelete', 'Are you sure you want to delete this language and its history?'))) {
      return;
    }
    try {
      await deleteTargetLanguage(id);
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete language');
    }
  };

  const handleQuickUpdateLevel = async (target: TargetLanguage, newLevel: LanguageLevel) => {
    if (target.level === newLevel) return;
    try {
      setLoading(true);
      await saveTargetLanguage({
        id: target.id,
        language_code: target.language_code,
        language_name: target.language_name,
        level: newLevel,
        daily_goal_minutes: target.daily_goal_minutes
      });
      setLevelChangeFeedback(`${t('langLearnLevelChangedSuccess', 'سطح تسلط زبان با موفقیت تغییر یافت.')} (${newLevel})`);
      setTimeout(() => setLevelChangeFeedback(null), 4500);
      try {
        // Auto-discover fresh smart plan with resources calibrated to new level
        await discoverDailyPlan({
          target_language_id: target.id,
          force_refresh: true
        });
      } catch (discErr) {
        console.warn('Could not auto-refresh plan for new level:', discErr);
      }
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to update level');
    } finally {
      setLoading(false);
    }
  };

  const openEditTargetModal = (target: TargetLanguage) => {
    setTargetBeingEdited(target);
    setEditTargetLevel(target.level);
    setEditTargetGoal(target.daily_goal_minutes || 45);
    setEditTargetWeakAreas(target.weak_areas || '');
    setEditTargetNotes(target.level_notes || '');
    setShowEditTargetModal(true);
  };

  const handleSaveTargetEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetBeingEdited) return;
    try {
      setLoading(true);
      const levelChanged = targetBeingEdited.level !== editTargetLevel;
      await saveTargetLanguage({
        id: targetBeingEdited.id,
        language_code: targetBeingEdited.language_code,
        language_name: targetBeingEdited.language_name,
        level: editTargetLevel,
        daily_goal_minutes: editTargetGoal,
        assessed_level: editTargetLevel,
        weak_areas: editTargetWeakAreas.trim() || undefined,
        level_notes: editTargetNotes.trim() || undefined
      });
      setShowEditTargetModal(false);
      setLevelChangeFeedback(`${t('langLearnLevelChangedSuccess', 'سطح تسلط زبان با موفقیت تغییر یافت.')} (${editTargetLevel})`);
      setTimeout(() => setLevelChangeFeedback(null), 4500);
      if (levelChanged) {
        try {
          await discoverDailyPlan({
            target_language_id: targetBeingEdited.id,
            force_refresh: true
          });
        } catch (discErr) {
          console.warn('Could not auto-refresh plan after level change:', discErr);
        }
      }
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // SMART PLAN DISCOVERY & REFRESH (Actively search web / curate external resources)
  // --------------------------------------------------------------------------
  const handleDiscoverSmartPlan = async () => {
    if (!currentTarget) return;
    try {
      setIsDiscoveringPlan(true);
      const res = await discoverDailyPlan({
        target_language_id: currentTarget.id,
        force_refresh: true
      });
      if (res.plan) {
        setDailyPlans(prev => prev.map(p => p.id === res.plan.id ? res.plan : p));
        if (res.analytics) setTargetAnalytics(res.analytics);
        setLevelChangeFeedback(t('langLearnPlanRefreshedSuccess', 'منابع هوشمند و وظایف روزانه با موفقیت به‌روزرسانی شدند.'));
        setTimeout(() => setLevelChangeFeedback(null), 4500);
      }
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to refresh smart plan');
    } finally {
      setIsDiscoveringPlan(false);
    }
  };

  // Open modal to mark plan item activity complete with difficulty & usefulness feedback
  const openCompleteActivityModal = (item: LanguageDailyPlanItem) => {
    setCompletingItem(item);
    setCompletionMinutes(item.duration_minutes || 15);
    setCompletionDifficulty(item.difficulty_feedback || 'good');
    setCompletionUsefulness(item.usefulness_feedback || 'very_useful');
    setCompletionNotes(item.user_notes || '');
  };

  // Submit plan activity completion to Cloudflare D1
  const handleSaveActivityCompletion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPlan || !completingItem) return;
    try {
      setIsSubmittingCompletion(true);
      const res = await completePlanActivity({
        plan_id: currentPlan.id,
        item_id: completingItem.id,
        skill: completingItem.skill,
        actual_minutes: Number(completionMinutes) || completingItem.duration_minutes || 15,
        difficulty_feedback: completionDifficulty,
        usefulness_feedback: completionUsefulness,
        user_notes: completionNotes.trim() || undefined
      });
      if (res.plan) {
        setDailyPlans(prev => prev.map(p => p.id === res.plan.id ? res.plan : p));
        if (res.analytics) setTargetAnalytics(res.analytics);
      }
      setCompletingItem(null);
      setLevelChangeFeedback(t('langLearnActivitySavedSuccess', 'تمرین با موفقیت ثبت شد و در پایگاه D1 ذخیره گردید.'));
      setTimeout(() => setLevelChangeFeedback(null), 4500);
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to record activity completion');
    } finally {
      setIsSubmittingCompletion(false);
    }
  };

  // --------------------------------------------------------------------------
  // 3. EDIT DAILY PLAN
  // --------------------------------------------------------------------------
  const [editListening, setEditListening] = useState(15);
  const [editSpeaking, setEditSpeaking] = useState(10);
  const [editReading, setEditReading] = useState(10);
  const [editWriting, setEditWriting] = useState(10);

  const openPlanEditor = () => {
    if (currentPlan) {
      setEditListening(currentPlan.listening_minutes);
      setEditSpeaking(currentPlan.speaking_minutes);
      setEditReading(currentPlan.reading_minutes);
      setEditWriting(currentPlan.writing_minutes);
    }
    setShowEditPlanModal(true);
  };

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPlan) return;
    try {
      const updated = await updateDailyPlan(currentPlan.id, {
        listening_minutes: editListening,
        speaking_minutes: editSpeaking,
        reading_minutes: editReading,
        writing_minutes: editWriting
      });
      setDailyPlans(prev => prev.map(p => (p.id === updated.id ? updated : p)));
      setShowEditPlanModal(false);
    } catch (err: any) {
      alert(err?.message || 'Failed to update plan');
    }
  };

  // --------------------------------------------------------------------------
  // 4. INTERACTIVE PRACTICE SESSION & AI GENERATION
  // --------------------------------------------------------------------------
  const startPractice = (skill: LanguageSkillType) => {
    setPracticeSkill(skill);
    // Set recommended duration from plan
    if (currentPlan) {
      const remaining = Math.max(5, (currentPlan[`${skill}_minutes` as keyof LanguageDailyPlan] as number) - (currentPlan[`${skill}_completed_minutes` as keyof LanguageDailyPlan] as number));
      setPracticeDuration(remaining > 0 ? remaining : 10);
    }
    setAiExercise(null);
    setUserSubmission('');
    setAiFeedback(null);
    setSelectedAnswers({});
    setSessionSaveSuccess(false);

    // Auto generate initial exercise for the chosen skill
    generateExerciseForSkill(skill);
  };

  const generateExerciseForSkill = async (skill: LanguageSkillType) => {
    if (!currentTarget) return;
    try {
      setIsAiGenerating(true);
      const srcLangName = profile?.source_language === 'fa' ? 'Persian' : profile?.source_language === 'sv' ? 'Swedish' : 'English';
      const exercise = await generateAiExercise({
        target_language: currentTarget.language_name,
        source_language: srcLangName,
        skill,
        level: currentTarget.level
      });
      setAiExercise(exercise);
      setSelectedAnswers({});
    } catch (err) {
      console.error('Failed to generate exercise:', err);
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Text-To-Speech (SpeechSynthesis)
  const playTTS = (textToPlay: string) => {
    if (!('speechSynthesis' in window)) {
      alert('Speech synthesis is not supported in this browser.');
      return;
    }
    if (isPlayingAudio) {
      window.speechSynthesis.cancel();
      setIsPlayingAudio(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(textToPlay);
    // Select voice matching target language
    const langCode = currentTarget?.language_code || 'sv';
    utterance.lang = langCode === 'sv' ? 'sv-SE' : langCode === 'en' ? 'en-US' : langCode === 'de' ? 'de-DE' : langCode === 'fr' ? 'fr-FR' : 'es-ES';
    utterance.rate = 0.9; // Slightly slower for clarity
    
    utterance.onend = () => setIsPlayingAudio(false);
    utterance.onerror = () => setIsPlayingAudio(false);

    setIsPlayingAudio(true);
    window.speechSynthesis.speak(utterance);
  };

  // Request AI Review / Correction
  const handleRequestAiReview = async () => {
    if (!userSubmission.trim() || !currentTarget) return;
    try {
      setIsAiEvaluating(true);
      const srcLangName = profile?.source_language === 'fa' ? 'Persian' : profile?.source_language === 'sv' ? 'Swedish' : 'English';
      const feedback = await submitForAiFeedback({
        target_language: currentTarget.language_name,
        source_language: srcLangName,
        skill: practiceSkill || 'writing',
        prompt: aiExercise?.instruction || aiExercise?.writingPrompt || aiExercise?.speakingPrompt,
        user_submission: userSubmission
      });
      setAiFeedback(feedback);
    } catch (err: any) {
      alert(err?.message || 'Failed to analyze submission');
    } finally {
      setIsAiEvaluating(false);
    }
  };

  // Complete and Record Session to D1
  const handleCompleteSession = async () => {
    if (!currentTarget || !practiceSkill) return;
    try {
      const title = aiExercise?.title || `${practiceSkill.toUpperCase()} Session`;
      const recorded = await recordPracticeSession({
        target_language_id: currentTarget.id,
        skill: practiceSkill,
        duration_minutes: practiceDuration,
        exercise_type: aiExercise ? 'ai_exercise' : 'practice',
        title,
        content_prompt: aiExercise?.content || aiExercise?.instruction,
        user_response: userSubmission || null,
        ai_feedback: aiFeedback ? JSON.stringify(aiFeedback) : null,
        score: aiFeedback?.score || (Object.keys(selectedAnswers).length > 0 ? 10 : null)
      });

      setSessionSaveSuccess(true);
      // Reload bootstrap to refresh daily plan, streak, and progress
      await loadData();

      setTimeout(() => {
        setPracticeSkill(null);
      }, 1200);
    } catch (err: any) {
      alert(err?.message || 'Failed to record practice session');
    }
  };

  // --------------------------------------------------------------------------
  // 5. RESOURCE MANAGEMENT
  // --------------------------------------------------------------------------
  const filteredResources = useMemo(() => {
    if (!currentTarget) return [];
    return resources.filter(r => {
      const matchLang = r.target_language_code === currentTarget.language_code || r.target_language_code === 'all';
      const matchSkill = resourceSkillFilter === 'all' || r.skill === resourceSkillFilter || r.skill === 'all';
      return matchLang && matchSkill;
    });
  }, [resources, currentTarget, resourceSkillFilter]);

  const handleAddResource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTarget || !newResTitle.trim() || !newResUrl.trim()) return;
    try {
      const created = await addCustomResource({
        target_language_code: currentTarget.language_code,
        skill: newResSkill,
        title: newResTitle.trim(),
        description: '',
        source_name: newResSource.trim() || 'Custom',
        url: newResUrl.trim(),
        difficulty_level: newResLevel
      });
      setResources(prev => [created, ...prev]);
      setShowAddResourceModal(false);
      setNewResTitle('');
      setNewResUrl('');
      setNewResSource('');
    } catch (err: any) {
      alert(err?.message || 'Failed to save resource');
    }
  };

  const handleDeleteResource = async (id: string) => {
    if (!confirm(t('confirmDelete', 'Delete this resource?'))) return;
    try {
      await deleteResource(id);
      setResources(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      alert(err?.message || 'Failed to delete resource');
    }
  };

  // Loading state
  if (loading && !profile && !showOnboarding) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 space-y-4">
        <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
        <p className="text-gray-600 dark:text-gray-300 font-medium">
          {t('loading', 'در حال بارگذاری اطلاعات آموزش زبان...')}
        </p>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // RENDER: ONBOARDING SCREEN
  // --------------------------------------------------------------------------
  if (showOnboarding) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6 my-6 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-2xl mb-4">
            <Globe className="w-10 h-10" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('langLearnSetupTitle', 'راه‌اندازی اولیه سامانه آموزش زبان')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mt-2 max-w-lg mx-auto">
            {t('langLearnSetupSubtitle', 'زبان مادری و زبان‌های هدفی که قصد یادگیری آن‌ها را دارید انتخاب کنید.')}
          </p>
        </div>

        <form onSubmit={handleCompleteOnboarding} className="space-y-6">
          {/* Source Language */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              {t('langLearnSourceLang', 'زبان مبدأ (زبان مادری شما)')}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {SUPPORTED_SOURCE_LANGS.map(item => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => setOnboardingSource(item.code)}
                  className={`py-3 px-4 rounded-xl border text-center font-medium transition-all ${
                    onboardingSource === item.code
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/20'
                      : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  {t(item.nameKey, item.code)}
                </button>
              ))}
            </div>
          </div>

          {/* Target Language Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              {t('langLearnTargetLangs', 'زبان هدف (زبان مورد نظر برای یادگیری)')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PRESET_TARGET_LANGS.map(item => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => setOnboardingTarget(item.code)}
                  className={`flex items-center gap-3 p-3 rounded-xl border font-medium transition-all ${
                    onboardingTarget === item.code
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/20'
                      : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <span className="text-2xl">{item.flag}</span>
                  <span className="text-sm font-semibold">{t(item.nameKey, item.defaultName)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Current Level */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              {t('langLearnTargetLangLevel', 'سطح تسلط فعلی (CEFR)')}
            </label>
            <div className="grid grid-cols-5 gap-2">
              {LEVELS.map(lvl => (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => setOnboardingLevel(lvl)}
                  className={`py-2 px-3 rounded-lg border text-center font-bold text-sm transition-all ${
                    onboardingLevel === lvl
                      ? 'border-emerald-500 bg-emerald-500 text-white shadow-md'
                      : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          {/* Daily Availability */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                {t('langLearnDailyAvail', 'زمان کل اختصاصی در روز')}
              </label>
              <span className="font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 rounded-full text-sm">
                {onboardingMinutes} {t('minutes', 'دقیقه')}
              </span>
            </div>
            <input
              type="range"
              min="15"
              max="120"
              step="5"
              value={onboardingMinutes}
              onChange={e => setOnboardingMinutes(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('langLearnDailyAvailDesc', 'سیستم به صورت خودکار این زمان را بین ۴ مهارت تقسیم خواهد کرد.')}
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] text-white rounded-xl font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            {t('langLearnStartBtn', 'شروع سفر یادگیری')}
          </button>
        </form>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // RENDER: MAIN APPLICATION VIEW
  // --------------------------------------------------------------------------
  return (
    <div className="space-y-6 pb-12">
      {/* Top Header Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-xl">
                <Globe className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
                  {t('languageLearningTitle', 'آموزش زبان')}
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  {t('languageLearningSubtitle', 'سیستم روزانه یادگیری زبان مبتنی بر مهارت‌های ۴ گانه')}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Metrics (Streak & Total Targets) */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40 px-3.5 py-1.5 rounded-xl font-bold text-sm">
              <Flame className="w-5 h-5 text-amber-500 fill-amber-500" />
              <span>
                {profile?.streak_days || 0} {t('days', 'روز')}
              </span>
            </div>

            <button
              onClick={() => setShowAddTargetModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded-xl border border-emerald-200 dark:border-emerald-800/50 font-semibold text-xs transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('langLearnAddTargetBtn', 'افزودن زبان جدید')}</span>
            </button>
          </div>
        </div>

        {/* Target Languages Switcher Bar (Multiple target languages supported simultaneously) */}
        {targets.length > 0 && (
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700/80 flex items-center justify-between gap-3 overflow-x-auto pb-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t('langLearnTargetLangs', 'زبان‌های شما')}:
              </span>
              <div className="flex items-center gap-2">
                {targets.map(target => {
                  const preset = PRESET_TARGET_LANGS.find(p => p.code === target.language_code);
                  const isSelected = target.id === selectedTargetId;
                  return (
                    <button
                      key={target.id}
                      onClick={() => setSelectedTargetId(target.id)}
                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                        isSelected
                          ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-650'
                      }`}
                    >
                      <span>{preset?.flag || '🌐'}</span>
                      <span>{target.language_name}</span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditTargetModal(target);
                        }}
                        title={t('langLearnChangeLevel', 'تغییر سطح تسلط')}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-black cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-white/20 text-white hover:bg-white/30'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-emerald-500 hover:text-white'
                        }`}
                      >
                        <span>{target.level}</span>
                        <Settings className="w-2.5 h-2.5 opacity-80" />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {currentTarget && (
                <button
                  onClick={() => openEditTargetModal(currentTarget)}
                  title={t('langLearnEditTargetBtn', 'ویرایش سطح و اهداف این زبان')}
                  className="text-gray-500 hover:text-emerald-600 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                </button>
              )}
              {currentTarget && targets.length > 1 && (
                <button
                  onClick={() => handleDeleteTarget(currentTarget.id)}
                  title={t('delete', 'حذف این زبان')}
                  className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Tabs inside Module */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('plan')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'plan'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>{t('langLearnTodayPlanTitle', 'برنامه تمرینی امروز')}</span>
        </button>

        <button
          onClick={() => setActiveTab('resources')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'resources'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          <ExternalLink className="w-4 h-4" />
          <span>{t('langLearnResourcesTitle', 'منابع یادگیری معتبر')}</span>
          <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full font-mono">
            {filteredResources.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('stats')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'stats'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>{t('langLearnStatsTitle', 'آمار و تاریخچه')}</span>
        </button>
      </div>

      {/* ---------------------------------------------------------------------- */}
      {/* TAB 1: TODAY'S LEARNING PLAN & 4 SKILLS PRACTICE GRID                  */}
      {/* ---------------------------------------------------------------------- */}
      {activeTab === 'plan' && currentTarget && currentPlan && (
        <div className="space-y-6">
          {/* Level Change Notification */}
          {levelChangeFeedback && (
            <div className="p-3.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 rounded-2xl text-xs sm:text-sm font-bold flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span>{levelChangeFeedback}</span>
              </div>
              <button
                onClick={() => setLevelChangeFeedback(null)}
                className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Today's Goal & Overview Banner */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-700 text-white rounded-2xl p-5 sm:p-6 shadow-md relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-emerald-100 text-xs uppercase tracking-wider font-bold mb-1">
                  <span className="text-sm font-black">{currentTarget.language_name}</span>
                  <span>•</span>
                  <span>{t('langLearnTargetLangLevel', 'سطح')}:</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded text-white font-black text-xs">{currentTarget.level}</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-black">
                  {t('langLearnTodayPlanTitle', 'برنامه تمرینی امروز زبان')}
                </h2>
                <p className="text-emerald-100 text-sm mt-1 max-w-xl">
                  {t('langLearnRecommendedSplit', 'تقسیم هوشمند زمان بر اساس مهارت‌های ۴ گانه: شنیداری، گفتاری، خوانداری و نوشتاری.')}
                </p>

                {/* Adaptive Focus Indicator */}
                {currentPlan.focus_skill && (
                  <div className="mt-2.5 inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-xl text-xs font-bold text-white border border-white/20">
                    <Target className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                    <span>{t('langLearnAdaptiveFocus', 'تمرکز تطبیقی امروز')}:</span>
                    <span className="text-amber-200 capitalize font-black">
                      {t(`langLearn${currentPlan.focus_skill.charAt(0).toUpperCase() + currentPlan.focus_skill.slice(1)}`, currentPlan.focus_skill)}
                    </span>
                    {currentPlan.focus_reason && (
                      <span className="opacity-90 font-normal border-r border-white/30 pr-1.5 mr-1 text-[11px] hidden sm:inline">
                        {currentPlan.focus_reason}
                      </span>
                    )}
                  </div>
                )}

                {/* Quick 1-Click Level Switcher */}
                <div className="mt-3.5 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-white flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                    <span>{t('langLearnChangeLevel', 'تغییر سطح تسلط')}:</span>
                  </span>
                  <div className="flex items-center gap-1 bg-black/25 backdrop-blur-sm p-1 rounded-xl border border-white/20">
                    {LEVELS.map(lvl => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => handleQuickUpdateLevel(currentTarget, lvl)}
                        className={`px-3 py-1 rounded-lg text-xs font-black transition-all ${
                          currentTarget.level === lvl
                            ? 'bg-white text-emerald-800 shadow-md ring-2 ring-white/60 scale-105'
                            : 'text-emerald-100 hover:bg-white/15 hover:text-white'
                        }`}
                        title={`${t('langLearnChangeLevel', 'تغییر سطح به')} ${lvl}`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => openEditTargetModal(currentTarget)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold transition-colors border border-white/20"
                    title={t('langLearnEditTargetBtn', 'تنظیمات و سطح زبان')}
                  >
                    <Settings className="w-3.5 h-3.5" />
                    <span>{t('edit', 'تنظیمات زبان')}</span>
                  </button>
                </div>
              </div>

              {/* Progress and Adjust Plan button */}
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm p-3 rounded-2xl border border-white/20">
                <div className="text-right">
                  <div className="text-xs text-emerald-100">
                    {t('total', 'کل زمان امروز')}
                  </div>
                  <div className="text-xl font-black">
                    {(currentPlan.listening_completed_minutes + currentPlan.speaking_completed_minutes + currentPlan.reading_completed_minutes + currentPlan.writing_completed_minutes)} / {currentPlan.total_planned_minutes} {t('minutes', 'دقیقه')}
                  </div>
                </div>
                <button
                  onClick={handleDiscoverSmartPlan}
                  disabled={isDiscoveringPlan}
                  className="p-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-colors font-bold text-xs flex items-center gap-1.5 shadow border border-white/20 disabled:opacity-50"
                  title={t('langLearnDiscoverSmartResources', 'جستجو و پیشنهاد منابع هوشمند جدید')}
                >
                  <RefreshCw className={`w-4 h-4 ${isDiscoveringPlan ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{t('langLearnRefreshPlan', 'به‌روزرسانی منابع')}</span>
                </button>
                <button
                  onClick={openPlanEditor}
                  className="p-2.5 bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl transition-colors font-bold text-xs flex items-center gap-1.5 shadow"
                  title={t('langLearnCustomisePlan', 'تنظیم دستی زمان')}
                >
                  <Sliders className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('edit', 'ویرایش')}</span>
                </button>
              </div>
            </div>

            {/* Overall Daily Progress Bar */}
            {(() => {
              const completed = currentPlan.listening_completed_minutes + currentPlan.speaking_completed_minutes + currentPlan.reading_completed_minutes + currentPlan.writing_completed_minutes;
              const total = currentPlan.total_planned_minutes || 45;
              const pct = Math.min(100, Math.round((completed / total) * 100));
              return (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="flex justify-between text-xs font-semibold mb-1.5">
                    <span>{t('progress', 'پیشرفت امروز')}: {pct}%</span>
                    {currentPlan.is_completed === 1 && (
                      <span className="flex items-center gap-1 text-emerald-200">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {t('langLearnAllSkillsDone', 'تمام مهارت‌ها تکمیل شد')}
                      </span>
                    )}
                  </div>
                  <div className="w-full h-2.5 bg-black/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* 4 CORE SKILLS PRACTICE CARDS GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(['listening', 'speaking', 'reading', 'writing'] as LanguageSkillType[]).map(skill => {
              const planned = currentPlan[`${skill}_minutes` as keyof LanguageDailyPlan] as number;
              const done = currentPlan[`${skill}_completed_minutes` as keyof LanguageDailyPlan] as number;
              const isFocus = currentPlan.focus_skill === skill;
              const planItem = currentPlan.items?.find(i => i.skill === skill);
              const isDone = (planItem?.is_completed) || (done >= planned && planned > 0);

              const config = {
                listening: {
                  name: t('langLearnListening', 'شنیداری (Listening)'),
                  icon: Headphones,
                  color: 'blue',
                  borderClass: 'hover:border-blue-400 dark:hover:border-blue-500',
                  iconBg: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
                  btnClass: 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/20'
                },
                speaking: {
                  name: t('langLearnSpeaking', 'گفتاری (Speaking)'),
                  icon: Mic,
                  color: 'purple',
                  borderClass: 'hover:border-purple-400 dark:hover:border-purple-500',
                  iconBg: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400',
                  btnClass: 'bg-purple-600 hover:bg-purple-700 text-white shadow-purple-600/20'
                },
                reading: {
                  name: t('langLearnReading', 'خوانداری (Reading)'),
                  icon: BookOpen,
                  color: 'amber',
                  borderClass: 'hover:border-amber-400 dark:hover:border-amber-500',
                  iconBg: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400',
                  btnClass: 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20'
                },
                writing: {
                  name: t('langLearnWriting', 'نوشتاری (Writing)'),
                  icon: PenTool,
                  color: 'emerald',
                  borderClass: 'hover:border-emerald-400 dark:hover:border-emerald-500',
                  iconBg: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400',
                  btnClass: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
                }
              }[skill];

              const IconComp = config.icon;

              return (
                <div
                  key={skill}
                  className={`bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col justify-between transition-all ${config.borderClass}`}
                >
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-2.5 rounded-xl ${config.iconBg}`}>
                          <IconComp className="w-5 h-5" />
                        </div>
                        {isFocus && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-black text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 rounded-md border border-amber-300/80 dark:border-amber-700/80">
                            <Target className="w-3 h-3" />
                            <span>{t('langLearnFocusBadge', 'تمرکز امروز')}</span>
                          </span>
                        )}
                      </div>

                      {isDone ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-800/60">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {t('completed', 'تکمیل')}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 rounded-full">
                          {done}/{planned} {t('minutes', 'دقیقه')}
                        </span>
                      )}
                    </div>

                    <div>
                      <h3 className="font-bold text-base text-gray-900 dark:text-white">
                        {config.name}
                      </h3>
                    </div>

                    {/* Discovered External Resource & Task Instruction */}
                    {planItem ? (
                      <div className="p-3 bg-gray-50 dark:bg-gray-750/70 rounded-xl border border-gray-100 dark:border-gray-700 text-xs space-y-2.5">
                        <div className="flex items-center justify-between gap-1 text-[11px]">
                          <span className="font-bold text-gray-600 dark:text-gray-300 truncate flex items-center gap-1">
                            <Globe className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="truncate">{planItem.source_name}</span>
                          </span>
                          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 shrink-0">
                            {planItem.resource_type}
                          </span>
                        </div>

                        {/* Direct External Link */}
                        <a
                          href={planItem.resource_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-start justify-between gap-1.5 group transition-colors"
                          title={planItem.resource_title}
                        >
                          <span className="line-clamp-2 underline decoration-blue-300 dark:decoration-blue-700 group-hover:decoration-blue-500">
                            {planItem.resource_title}
                          </span>
                          <ExternalLink className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                        </a>

                        {/* Task Instruction Box */}
                        <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 space-y-1">
                          <div className="text-[11px] font-medium text-gray-800 dark:text-gray-200 leading-relaxed">
                            <span className="text-emerald-600 dark:text-emerald-400 font-black ml-1">
                              {t('langLearnTaskPrefix', 'وظیفه:')}
                            </span>
                            {planItem.task_instruction}
                          </div>
                          {planItem.why_selected && (
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                              {planItem.why_selected}
                            </div>
                          )}
                        </div>

                        {/* Completion Feedback Badges */}
                        {planItem.is_completed && (
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5 text-[10px]">
                            <span className="bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded font-bold">
                              ✓ {planItem.actual_minutes || planItem.duration_minutes} {t('min', 'دقیقه')}
                            </span>
                            {planItem.difficulty_feedback && (
                              <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                                {planItem.difficulty_feedback === 'too_easy' ? 'آسان' : planItem.difficulty_feedback === 'good' ? 'مناسب' : 'سخت'}
                              </span>
                            )}
                            {planItem.usefulness_feedback && (
                              <span className="bg-teal-100 dark:bg-teal-950/60 text-teal-800 dark:text-teal-300 px-1.5 py-0.5 rounded">
                                {planItem.usefulness_feedback === 'very_useful' ? 'عالی' : planItem.usefulness_feedback === 'useful' ? 'مفید' : 'کم‌فایده'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                        {t(`langLearn${skill.charAt(0).toUpperCase() + skill.slice(1)}Desc`, '')}
                      </p>
                    )}
                  </div>

                  {/* Actions Area */}
                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                    {planItem && (
                      <div className="space-y-1.5">
                        {!isDone ? (
                          <button
                            onClick={() => openCompleteActivityModal(planItem)}
                            className="w-full py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-sm shadow-emerald-600/20"
                          >
                            <Check className="w-3.5 h-3.5 stroke-[3]" />
                            <span>{t('langLearnMarkCompleted', 'ثبت اتمام تمرین و بازخورد')}</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => openCompleteActivityModal(planItem)}
                            className="w-full py-1.5 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-650 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Star className="w-3.5 h-3.5 text-amber-500" />
                            <span>{t('langLearnEditFeedback', 'ویرایش بازخورد یا زمان')}</span>
                          </button>
                        )}

                        <div className="flex items-center gap-1.5">
                          <a
                            href={planItem.resource_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 py-1.5 px-2 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-1 border border-blue-200/80 dark:border-blue-800/50"
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>{t('langLearnOpenResource', 'ورود به منبع')}</span>
                          </a>

                          <button
                            onClick={() => startPractice(skill)}
                            className="py-1.5 px-2.5 text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors flex items-center gap-1"
                            title={t('langLearnPracticeAi', 'تمرین درون‌برنامه')}
                          >
                            <Play className="w-3 h-3" />
                            <span className="hidden xl:inline">{t('langLearnPracticeAi', 'تمرین درون‌برنامه')}</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {!planItem && (
                      <button
                        onClick={() => startPractice(skill)}
                        className={`w-full py-2 px-3 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-sm ${config.btnClass}`}
                      >
                        <Play className="w-3.5 h-3.5" />
                        <span>{t('langLearnStartPractice', 'شروع تمرین')}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* TAB 2: CURATED & CUSTOM EXTERNAL LEARNING RESOURCES                   */}
      {/* ---------------------------------------------------------------------- */}
      {activeTab === 'resources' && currentTarget && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white text-base">
                {t('langLearnResourcesTitle', 'منابع و پایگاه‌های یادگیری معتبر')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('langLearnResourcesSubtitle', 'لینک‌های معتبر و طبقه‌بندی‌شده برای تقویت مهارت‌ها')} ({currentTarget.language_name})
              </p>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={resourceSkillFilter}
                onChange={e => setResourceSkillFilter(e.target.value)}
                className="bg-gray-100 dark:bg-gray-700 border-none text-xs font-semibold rounded-xl px-3 py-2 text-gray-700 dark:text-gray-200"
              >
                <option value="all">{t('langLearnFilterAll', 'همه مهارت‌ها')}</option>
                <option value="listening">{t('langLearnListening', 'شنیداری')}</option>
                <option value="speaking">{t('langLearnSpeaking', 'گفتاری')}</option>
                <option value="reading">{t('langLearnReading', 'خوانداری')}</option>
                <option value="writing">{t('langLearnWriting', 'نوشتاری')}</option>
              </select>

              <button
                onClick={() => setShowAddResourceModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>{t('langLearnAddResourceBtn', 'افزودن لینک منبع')}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredResources.map(res => {
              const skillIcons: Record<string, any> = {
                listening: Headphones,
                speaking: Mic,
                reading: BookOpen,
                writing: PenTool,
                all: Globe
              };
              const IconComp = skillIcons[res.skill] || Globe;

              return (
                <div
                  key={res.id}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col justify-between hover:border-emerald-300 dark:hover:border-emerald-600 transition-all"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-lg">
                          <IconComp className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                          {res.source_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                          {res.difficulty_level}
                        </span>
                        {res.is_custom === 1 && (
                          <button
                            onClick={() => handleDeleteResource(res.id)}
                            className="text-gray-400 hover:text-red-500 p-1 rounded"
                            title={t('langLearnDeleteResource', 'حذف منبع')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <h4 className="font-bold text-gray-900 dark:text-white text-base">
                      {res.title}
                    </h4>
                    {res.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        {res.description}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/80 flex justify-end">
                    <a
                      href={res.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                    >
                      <span>{t('langLearnVisitResource', 'مشاهده منبع')}</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* TAB 3: OVERALL PROGRESS & PRACTICE HISTORY                            */}
      {/* ---------------------------------------------------------------------- */}
      {activeTab === 'stats' && currentTarget && (
        <div className="space-y-6">
          {/* Skill breakdown stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {(['listening', 'speaking', 'reading', 'writing'] as LanguageSkillType[]).map(skill => {
              const prog = currentSkillProgress.find(p => p.skill === skill);
              const totalMin = prog?.total_minutes || 0;
              const sessions = prog?.total_sessions || 0;
              const skillIcons: Record<string, any> = {
                listening: Headphones,
                speaking: Mic,
                reading: BookOpen,
                writing: PenTool
              };
              const IconComp = skillIcons[skill];

              return (
                <div
                  key={skill}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
                      <IconComp className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300 capitalize">
                      {t(`langLearn${skill.charAt(0).toUpperCase() + skill.slice(1)}`, skill)}
                    </span>
                  </div>
                  <div className="text-2xl font-black text-gray-900 dark:text-white">
                    {totalMin} <span className="text-xs font-normal text-gray-500">{t('minutes', 'دقیقه')}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {sessions} {t('langLearnSessionsCount', 'جلسه ثبت شده')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Session History Log */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="font-bold text-gray-900 dark:text-white text-base mb-4">
              {t('langLearnHistoryTitle', 'تاریخچه جلسات ثبت‌شده')}
            </h3>

            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : historySessions.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {t('langLearnNoHistory', 'هنوز جلسه‌ای برای این زبان ثبت نشده است. اولین تمرین امروز خود را آغاز کنید!')}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {historySessions.map(session => (
                  <div key={session.id} className="py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                          {session.skill}
                        </span>
                        <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                          {session.title}
                        </h4>
                      </div>
                      {session.user_response && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1 italic">
                          "{session.user_response}"
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 self-end sm:self-center">
                      {session.score && (
                        <span className="font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 rounded">
                          {session.score}/10
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {session.duration_minutes} {t('min', 'دقیقه')}
                      </span>
                      <span>{session.session_date}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* MODAL: INTERACTIVE PRACTICE SESSION                                    */}
      {/* ---------------------------------------------------------------------- */}
      {practiceSkill && currentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 my-8 overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-750">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-xl">
                  {practiceSkill === 'listening' ? <Headphones className="w-5 h-5" /> : practiceSkill === 'speaking' ? <Mic className="w-5 h-5" /> : practiceSkill === 'reading' ? <BookOpen className="w-5 h-5" /> : <PenTool className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="font-bold text-base text-gray-900 dark:text-white">
                    {t('langLearnPracticeTitle', 'اتاق تمرین تعاملی')} — {t(`langLearn${practiceSkill.charAt(0).toUpperCase() + practiceSkill.slice(1)}`, practiceSkill)}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {currentTarget.language_name} ({currentTarget.level})
                  </p>
                </div>
              </div>
              <button
                onClick={() => setPracticeSkill(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-6">
              {/* Duration selector & AI Generator Trigger */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    {t('duration', 'مدت تمرین')}:
                  </span>
                  <div className="flex items-center gap-1.5">
                    {[5, 10, 15, 20].map(mins => (
                      <button
                        key={mins}
                        onClick={() => setPracticeDuration(mins)}
                        className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors ${
                          practiceDuration === mins
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {mins} {t('min', 'دقیقه')}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => generateExerciseForSkill(practiceSkill)}
                  disabled={isAiGenerating}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{isAiGenerating ? t('langLearnAiGenerating', 'در حال تولید...') : t('langLearnGenerateAiExercise', 'تمرین جدید هوش مصنوعی')}</span>
                </button>
              </div>

              {/* AI Generated Practice Content */}
              {isAiGenerating ? (
                <div className="flex flex-col items-center justify-center py-10 space-y-3">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t('langLearnAiGenerating', 'در حال تولید تمرین متناسب با سطح شما...')}
                  </p>
                </div>
              ) : aiExercise ? (
                <div className="space-y-4">
                  <div className="border border-emerald-100 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 rounded-xl">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h4 className="font-bold text-emerald-900 dark:text-emerald-200 text-sm">
                        {aiExercise.title}
                      </h4>
                      {aiExercise.content && (
                        <button
                          onClick={() => playTTS(aiExercise.content)}
                          className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg transition-colors ${
                            isPlayingAudio
                              ? 'bg-red-500 text-white'
                              : 'bg-emerald-600 text-white hover:bg-emerald-700'
                          }`}
                        >
                          {isPlayingAudio ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                          <span>{isPlayingAudio ? t('stop', 'توقف صوتی') : t('langLearnAudioListen', 'شنیدن متن')}</span>
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-emerald-800 dark:text-emerald-300/80 mb-3">
                      {aiExercise.instruction}
                    </p>
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-lg text-sm text-gray-800 dark:text-gray-100 leading-relaxed font-medium border border-emerald-100/50 dark:border-gray-700">
                      {aiExercise.content}
                    </div>
                  </div>

                  {/* Comprehension Questions if provided */}
                  {aiExercise.questions && aiExercise.questions.length > 0 && (
                    <div className="space-y-3">
                      <h5 className="font-bold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        {t('questions', 'پرسش‌های درک مطلب')}:
                      </h5>
                      {aiExercise.questions.map((q, qIndex) => {
                        const isAnswered = selectedAnswers[qIndex] !== undefined;
                        const userAns = selectedAnswers[qIndex];
                        return (
                          <div key={qIndex} className="p-3.5 bg-gray-50 dark:bg-gray-750 rounded-xl space-y-2 border border-gray-100 dark:border-gray-700">
                            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                              {qIndex + 1}. {q.question}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                              {q.options.map((opt, optIdx) => {
                                let btnClass = 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700';
                                if (isAnswered) {
                                  if (optIdx === q.correctIndex) {
                                    btnClass = 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-bold';
                                  } else if (userAns === optIdx) {
                                    btnClass = 'border-red-400 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 line-through';
                                  }
                                }
                                return (
                                  <button
                                    key={optIdx}
                                    onClick={() => setSelectedAnswers(prev => ({ ...prev, [qIndex]: optIdx }))}
                                    className={`p-2 rounded-lg border text-xs text-left transition-all ${btnClass}`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                            {isAnswered && q.explanation && (
                              <p className="text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-2 rounded mt-1">
                                💡 {q.explanation}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Vocabulary bank */}
                  {aiExercise.vocabularyList && aiExercise.vocabularyList.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-bold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        {t('vocabulary', 'واژگان کلیدی')}:
                      </h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {aiExercise.vocabularyList.map((v, vIdx) => (
                          <div key={vIdx} className="p-2.5 bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-100 dark:border-gray-700 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-emerald-600 dark:text-emerald-400">{v.word}</span>
                              <span className="text-gray-600 dark:text-gray-300">{v.meaning}</span>
                            </div>
                            {v.example && (
                              <p className="text-[11px] text-gray-500 mt-1 italic">"{v.example}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* User Response / Submission Input (Writing or Speaking) */}
              <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    {t('langLearnWriteResponse', 'پاسخ یا نگارش متن شما')}:
                  </label>
                  <div className="flex items-center gap-2">
                    <VoiceInputButton
                      currentValue={userSubmission}
                      onUpdate={setUserSubmission}
                      title={t('langLearnVoiceInputTip', 'تبدیل صوت به متن')}
                    />
                  </div>
                </div>

                <textarea
                  rows={3}
                  value={userSubmission}
                  onChange={e => setUserSubmission(e.target.value)}
                  placeholder={practiceSkill === 'speaking' ? t('langLearnSpeakNow', 'متن گفته‌شده خود را بنویسید یا با دکمه میکروفون ضبط کنید...') : t('langLearnWriteResponse', 'جملات یا انشای خود را به زبان هدف اینجا بنویسید...')}
                  className="w-full p-3 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-gray-900 dark:text-white"
                />

                {userSubmission.trim().length > 5 && (
                  <button
                    onClick={handleRequestAiReview}
                    disabled={isAiEvaluating}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40 hover:bg-purple-100 dark:hover:bg-purple-900/60 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{isAiEvaluating ? t('checking', 'در حال ارزیابی با AI...') : t('langLearnRequestAiFeedback', 'بررسی و تصحیح هوشمند با AI')}</span>
                  </button>
                )}
              </div>

              {/* AI Feedback Display */}
              {aiFeedback && (
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800/40 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-purple-900 dark:text-purple-200 text-xs">
                      {t('langLearnAiFeedbackTitle', 'بازخورد، تصحیح و نکات گرامری')}
                    </span>
                    <span className="font-black text-sm bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-100 px-2.5 py-0.5 rounded-full">
                      {aiFeedback.score}/10
                    </span>
                  </div>

                  {aiFeedback.correctedText && (
                    <div className="text-xs">
                      <span className="font-bold text-gray-600 dark:text-gray-400">{t('langLearnCorrected', 'متن اصلاح‌شده:')}</span>
                      <p className="p-2 bg-white dark:bg-gray-800 rounded mt-1 font-medium text-emerald-600 dark:text-emerald-400">
                        {aiFeedback.correctedText}
                      </p>
                    </div>
                  )}

                  {aiFeedback.corrections && aiFeedback.corrections.length > 0 && (
                    <div className="space-y-1.5">
                      <span className="text-[11px] font-bold text-gray-500 uppercase">{t('langLearnCorrections', 'اصلاحات نگارشی:')}</span>
                      {aiFeedback.corrections.map((corr, idx) => (
                        <div key={idx} className="p-2 bg-white/60 dark:bg-gray-800/60 rounded text-xs space-y-0.5">
                          <div>
                            <span className="text-red-500 line-through">{corr.original}</span>
                            <span className="mx-1 text-gray-400">➔</span>
                            <span className="text-emerald-600 font-bold">{corr.correction}</span>
                          </div>
                          <p className="text-[11px] text-gray-500">{corr.reason}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {aiFeedback.tipsForImprovement && (
                    <p className="text-xs text-purple-800 dark:text-purple-300 font-medium">
                      💡 {t('langLearnImprovementTip', 'نکته آموزشی:')} {aiFeedback.tipsForImprovement}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-750">
              <span className="text-xs text-gray-500">
                {sessionSaveSuccess ? (
                  <span className="text-emerald-600 font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    {t('langLearnSessionSaved', 'جلسه با موفقیت در فضای ابری ذخیره شد')}
                  </span>
                ) : (
                  <span>{t('duration', 'مدت')}: {practiceDuration} {t('minutes', 'دقیقه')}</span>
                )}
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPracticeSkill(null)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  {t('close', 'بستن')}
                </button>
                <button
                  onClick={handleCompleteSession}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-600/20 flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{t('langLearnCompleteSession', 'ثبت و اتمام جلسه')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* MODAL: EDIT DAILY PLAN ALLOCATION                                      */}
      {/* ---------------------------------------------------------------------- */}
      {showEditPlanModal && currentPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                {t('langLearnCustomisePlan', 'تغییر دستی زمان‌بندی مهارت‌ها')}
              </h3>
              <button onClick={() => setShowEditPlanModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePlan} className="space-y-4">
              <div>
                <div className="flex justify-between text-xs font-semibold mb-1">
                  <span>{t('langLearnListening', 'شنیداری')}</span>
                  <span>{editListening} {t('min', 'دقیقه')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="60"
                  step="5"
                  value={editListening}
                  onChange={e => setEditListening(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-semibold mb-1">
                  <span>{t('langLearnSpeaking', 'گفتاری')}</span>
                  <span>{editSpeaking} {t('min', 'دقیقه')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="60"
                  step="5"
                  value={editSpeaking}
                  onChange={e => setEditSpeaking(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-semibold mb-1">
                  <span>{t('langLearnReading', 'خوانداری')}</span>
                  <span>{editReading} {t('min', 'دقیقه')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="60"
                  step="5"
                  value={editReading}
                  onChange={e => setEditReading(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-600"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-semibold mb-1">
                  <span>{t('langLearnWriting', 'نوشتاری')}</span>
                  <span>{editWriting} {t('min', 'دقیقه')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="60"
                  step="5"
                  value={editWriting}
                  onChange={e => setEditWriting(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                />
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl flex justify-between items-center text-xs font-bold text-gray-700 dark:text-gray-200">
                <span>{t('total', 'مجموع زمان:')}</span>
                <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                  {editListening + editSpeaking + editReading + editWriting} {t('minutes', 'دقیقه')}
                </span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditPlanModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
                >
                  {t('cancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20"
                >
                  {t('save', 'ذخیره')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* MODAL: ADD NEW TARGET LANGUAGE                                         */}
      {/* ---------------------------------------------------------------------- */}
      {showAddTargetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                {t('langLearnAddTargetBtn', 'افزودن زبان هدف جدید')}
              </h3>
              <button onClick={() => setShowAddTargetModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddTarget} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('langLearnTargetLangName', 'نام زبان هدف')}
                </label>
                <select
                  value={newTargetCode}
                  onChange={e => setNewTargetCode(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-medium text-gray-900 dark:text-white"
                >
                  {PRESET_TARGET_LANGS.map(p => (
                    <option key={p.code} value={p.code}>
                      {p.flag} {t(p.nameKey, p.defaultName)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('langLearnTargetLangLevel', 'سطح تسلط فعلی (CEFR)')}
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {LEVELS.map(lvl => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setNewTargetLevel(lvl)}
                      className={`py-1.5 text-xs font-bold rounded-lg border transition-all ${
                        newTargetLevel === lvl
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('langLearnDailyGoalMinutes', 'هدف روزانه (دقیقه)')}
                </label>
                <input
                  type="number"
                  min="15"
                  max="180"
                  step="5"
                  value={newTargetGoal}
                  onChange={e => setNewTargetGoal(Number(e.target.value))}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-medium text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddTargetModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
                >
                  {t('cancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20"
                >
                  {t('add', 'افزودن')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* MODAL: EDIT TARGET LANGUAGE & LEVEL (e.g. A2 -> B1 / B2)               */}
      {/* ---------------------------------------------------------------------- */}
      {showEditTargetModal && targetBeingEdited && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-xl">
                  <Settings className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-gray-900 dark:text-white">
                    {t('langLearnEditTargetBtn', 'تنظیمات و تغییر سطح زبان')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {targetBeingEdited.language_name}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowEditTargetModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTargetEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('langLearnTargetLangLevel', 'سطح تسلط فعلی (CEFR)')}
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {LEVELS.map(lvl => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setEditTargetLevel(lvl)}
                      className={`py-2 text-xs font-black rounded-xl border transition-all flex flex-col items-center gap-0.5 ${
                        editTargetLevel === lvl
                          ? 'border-emerald-500 bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 text-gray-700 dark:text-gray-300 hover:border-emerald-400'
                      }`}
                    >
                      <span className="text-sm font-black">{lvl}</span>
                      <span className="text-[9px] font-normal opacity-80">
                        {lvl === 'A1' ? 'مبتدی' : lvl === 'A2' ? 'مقدماتی' : lvl === 'B1' ? 'متوسط' : lvl === 'B2' ? 'فوق‌متوسط' : 'پیشرفته'}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  با انتخاب سطح جدید (مانند B1 یا B2)، کلیه تمرینات هوشمند AI، متون و سوالات به صورت خودکار بر اساس سطح انتخابی تنظیم می‌شوند.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('langLearnDailyGoalMinutes', 'هدف روزانه این زبان (دقیقه)')}
                </label>
                <input
                  type="number"
                  min="15"
                  max="180"
                  step="5"
                  value={editTargetGoal}
                  onChange={e => setEditTargetGoal(Number(e.target.value))}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-medium text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('langLearnWeakAreas', 'نقاط نیازمند تمرکز بیشتر (اختیاری)')}
                </label>
                <input
                  type="text"
                  placeholder="مثلاً: مکالمه و ادای کلمات، گرامر افعال، واژگان روزمره، اصطلاحات کاری"
                  value={editTargetWeakAreas}
                  onChange={e => setEditTargetWeakAreas(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-medium text-gray-900 dark:text-white"
                />
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                  سیستم هوشمند این اطلاعات را در اولویت‌بندی منابع روزانه اعمال خواهد کرد.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('langLearnLevelNotes', 'یادداشت یا توضیحات سطح شما (اختیاری)')}
                </label>
                <textarea
                  rows={2}
                  placeholder="مثلاً: قبلاً دوره A2 را گذرانده‌ام و می‌خواهم برای آزمون B1 آماده شوم"
                  value={editTargetNotes}
                  onChange={e => setEditTargetNotes(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-medium text-gray-900 dark:text-white resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditTargetModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
                >
                  {t('cancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20"
                >
                  {t('save', 'ذخیره تغییرات')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* MODAL: ADD CUSTOM EXTERNAL RESOURCE                                    */}
      {/* ---------------------------------------------------------------------- */}
      {showAddResourceModal && currentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                {t('langLearnAddResourceBtn', 'افزودن لینک منبع جدید')}
              </h3>
              <button onClick={() => setShowAddResourceModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddResource} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('langLearnResourceTitle', 'عنوان منبع')}
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. SVT Play / BBC Podcast"
                  value={newResTitle}
                  onChange={e => setNewResTitle(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('langLearnResourceUrl', 'آدرس اینترنتی (URL)')}
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://..."
                  value={newResUrl}
                  onChange={e => setNewResUrl(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('langLearnResourceSource', 'نام پایگاه / منبع')}
                </label>
                <input
                  type="text"
                  placeholder="e.g. SVT / Sveriges Radio / Cambridge"
                  value={newResSource}
                  onChange={e => setNewResSource(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    {t('skill', 'مهارت')}
                  </label>
                  <select
                    value={newResSkill}
                    onChange={e => setNewResSkill(e.target.value as any)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white"
                  >
                    <option value="all">{t('langLearnFilterAll', 'همه مهارت‌ها')}</option>
                    <option value="listening">{t('langLearnListening', 'شنیداری')}</option>
                    <option value="speaking">{t('langLearnSpeaking', 'گفتاری')}</option>
                    <option value="reading">{t('langLearnReading', 'خوانداری')}</option>
                    <option value="writing">{t('langLearnWriting', 'نوشتاری')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    {t('langLearnDifficultyLevel', 'سطح دشواری')}
                  </label>
                  <select
                    value={newResLevel}
                    onChange={e => setNewResLevel(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white"
                  >
                    <option value="all">{t('langLearnLevelAll', 'همه سطوح')}</option>
                    <option value="A1-A2">A1 - A2</option>
                    <option value="B1-B2">B1 - B2</option>
                    <option value="C1">C1</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddResourceModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
                >
                  {t('cancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20"
                >
                  {t('save', 'ذخیره منبع')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* MODAL: SMART ACTIVITY COMPLETION & FEEDBACK                            */}
      {/* ---------------------------------------------------------------------- */}
      {completingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-2xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-xl">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-gray-900 dark:text-white">
                    {t('langLearnLogActivityTitle', 'ثبت انجام تمرین و ارزیابی')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {completingItem.resource_title}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCompletingItem(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Task summary */}
            <div className="p-3 bg-gray-50 dark:bg-gray-750 rounded-xl text-xs space-y-1 border border-gray-100 dark:border-gray-700">
              <div className="font-bold text-gray-700 dark:text-gray-300">
                {t('langLearnTaskDone', 'دستورالعمل تمرین:')}
              </div>
              <div className="text-gray-600 dark:text-gray-300">
                {completingItem.task_instruction}
              </div>
            </div>

            {/* Actual Minutes Spent */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                {t('langLearnActualMinutes', 'مدت زمان واقعی تمرین (دقیقه)')}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={completionMinutes}
                  onChange={e => setCompletionMinutes(Math.max(1, Number(e.target.value)))}
                  className="w-28 p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-bold text-gray-900 dark:text-white"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('minutes', 'دقیقه')}
                </span>
              </div>
            </div>

            {/* Difficulty Feedback */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                {t('langLearnDifficultyFeedback', 'سطح دشواری این تمرین چطور بود؟')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'too_easy' as const, label: 'آسان بود', icon: '🟢', sub: 'ساده‌تر از سطح من' },
                  { id: 'good' as const, label: 'مناسب و استاندارد', icon: '🎯', sub: 'دقیقاً متناسب من' },
                  { id: 'too_hard' as const, label: 'سخت و چالش‌برانگیز', icon: '🔴', sub: 'فراتر از سطح فعلی' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCompletionDifficulty(opt.id)}
                    className={`p-2.5 rounded-xl border text-xs font-bold flex flex-col items-center gap-1 transition-all ${
                      completionDifficulty === opt.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-200 ring-2 ring-emerald-500/30'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <span>{opt.icon}</span>
                    <span>{opt.label}</span>
                    <span className="text-[10px] opacity-75 font-normal">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Usefulness Feedback */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                {t('langLearnUsefulnessFeedback', 'کیفیت و میزان مفید بودن این منبع')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'very_useful' as const, label: 'بسیار مفید', sub: 'پیشنهاد عالی بود' },
                  { id: 'useful' as const, label: 'مفید', sub: 'خوب و کاربردی' },
                  { id: 'not_useful' as const, label: 'کم‌فایده', sub: 'علاقه نداشتم' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCompletionUsefulness(opt.id)}
                    className={`p-2 rounded-xl border text-xs font-bold flex flex-col items-center gap-0.5 transition-all ${
                      completionUsefulness === opt.id
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/50 text-teal-800 dark:text-teal-200 ring-2 ring-teal-500/30'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <span>{opt.label}</span>
                    <span className="text-[10px] opacity-75 font-normal">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                {t('langLearnNotesOptional', 'یادداشت، لغات جدید یا نکات کلیدی (اختیاری)')}
              </label>
              <textarea
                rows={2}
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                placeholder="مثلاً: ۳ واژه جدید یاد گرفتم، بخش دوم پادکست سرعت بالایی داشت..."
                className="w-full p-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white resize-none"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2.5 pt-2 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setCompletingItem(null)}
                className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
              >
                {t('cancel', 'انصراف')}
              </button>
              <button
                type="button"
                onClick={handleSaveActivityCompletion}
                disabled={isSubmittingCompletion}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20 flex items-center gap-1.5 disabled:opacity-50"
              >
                {isSubmittingCompletion ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 stroke-[3]" />
                )}
                <span>{t('langLearnSaveActivityCompletion', 'ثبت تمرین در پایگاه داده')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
